import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Chip,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { adminService } from '../../services/adminService';

const BoutiqueModal = ({ open, onClose, onSuccess, boutique = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    quartier: '',
    phone: '',
    whatsapp: '',
    email: '',
    openingHours: '',
    specialties: [],
    vendorId: '',
    logo: '',
    isActive: true
  });
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorBoutiques, setSelectedVendorBoutiques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [specialtyOptions, setSpecialtyOptions] = useState([
    'Mode', 'Accessoires', 'Chaussures', 'Bijoux', 'Cosmétiques',
    'Électronique', 'Maison', 'Sport', 'Livre', 'Alimentation'
  ].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' })));

  useEffect(() => {
    if (open) {
      loadVendors();
      loadSpecialties();
      if (boutique) {
        setFormData({
          name: boutique.name || '',
          description: boutique.description || '',
          address: boutique.address || '',
          quartier: boutique.quartier || '',
          phone: boutique.phone || '',
          whatsapp: boutique.whatsapp || '',
          email: boutique.email || '',
          openingHours: boutique.openingHours || '',
          specialties: boutique.specialties || [],
          vendorId: boutique.vendorId || '',
          logo: boutique.logo || '',
          isActive: boutique.isActive !== false
        });
        setLogoPreview(boutique.logo || '');
      } else {
        setFormData({
          name: '',
          description: '',
          address: '',
          quartier: '',
          phone: '',
          whatsapp: '',
          email: '',
          openingHours: '',
          specialties: [],
          vendorId: '',
          logo: '',
          isActive: true
        });
        setLogoPreview('');
      }
    }
  }, [boutique, open]);

  useEffect(() => {
    if (vendors.length > 0 && boutique) {
      const vendor = vendors.find(v => v.id === boutique.vendorId);
      setSelectedVendor(vendor || null);
      if (vendor) {
        loadVendorBoutiques(vendor.id);
      }
    } else if (!boutique) {
      setSelectedVendor(null);
      setSelectedVendorBoutiques([]);
    }
  }, [vendors, boutique]);

  const loadVendors = async () => {
    setLoadingVendors(true);
    try {
      const data = await adminService.getUsers({ 
        role: 'VENDOR',
        isApproved: true,
        isBlocked: false
      });
      setVendors(data.users || []);
    } catch (err) {
      console.error('Erreur lors du chargement des vendeurs:', err);
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };

  const loadVendorBoutiques = async (vendorId) => {
    if (!vendorId) {
      setSelectedVendorBoutiques([]);
      return;
    }
    try {
      const data = await adminService.getBoutiques({ vendorId });
      setSelectedVendorBoutiques(data.boutiques || []);
    } catch (err) {
      console.error('Erreur lors du chargement des boutiques du vendeur:', err);
      setSelectedVendorBoutiques([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVendorChange = (event, newValue) => {
    setSelectedVendor(newValue);
    const vendorId = newValue ? newValue.id : '';
    setFormData(prev => ({
      ...prev,
      vendorId: vendorId
    }));
    // Charger les boutiques du vendeur sélectionné
    loadVendorBoutiques(vendorId);
  };

  const loadSpecialties = async () => {
    try {
      const response = await adminService.getSystemSetting('boutique_specialties');
      if (response && response.value && Array.isArray(response.value)) {
        // Trier les spécialités par ordre alphabétique
        const sortedSpecialties = [...response.value].sort((a, b) => 
          a.localeCompare(b, 'fr', { sensitivity: 'base' })
        );
        setSpecialtyOptions(sortedSpecialties);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des spécialités:', err);
      // Garder les valeurs par défaut en cas d'erreur
    }
  };

  const handleSpecialtiesChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      specialties: newValue
    }));
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Seules les images (JPEG, JPG, PNG, GIF, WEBP) sont autorisées');
      return;
    }

    // Vérifier la taille (50MB max - sera compressé en WebP)
    if (file.size > 50 * 1024 * 1024) {
      setError('Le fichier est trop volumineux (max 50MB)');
      return;
    }

    setLogoFile(file);
    setError('');

    // Créer un aperçu
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Uploader le fichier
    setUploadingLogo(true);
    try {
      const response = await adminService.uploadBoutiqueLogo(file);
      setFormData(prev => ({
        ...prev,
        logo: response.logoUrl
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'upload du logo');
      setLogoFile(null);
      setLogoPreview('');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Vérifier la limite de boutiques pour les nouvelles boutiques
    if (!boutique && selectedVendorBoutiques.length >= 4) {
      setError('Ce vendeur a déjà atteint la limite de 4 boutiques maximum.');
      setLoading(false);
      return;
    }

    try {
      if (boutique) {
        await adminService.updateBoutique(boutique.id, formData);
        onSuccess('Boutique modifiée avec succès');
      } else {
        await adminService.createBoutique(formData);
        onSuccess('Boutique créée avec succès');
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {boutique ? 'Modifier la boutique' : 'Créer une boutique'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={vendors}
              value={selectedVendor}
              onChange={handleVendorChange}
              getOptionLabel={(option) =>
                option ? `${option.name} (${option.email || option.phone || 'N/A'})` : ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={loadingVendors}
              disabled={loadingVendors}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Propriétaire"
                  required
                  placeholder="Rechercher un vendeur..."
                  helperText="Tapez pour rechercher un vendeur"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1" fontWeight="medium">
                    {option.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.email || option.phone || 'N/A'}
                  </Typography>
                </Box>
              )}
              noOptionsText={
                loadingVendors
                  ? "Chargement des vendeurs..."
                  : "Aucun vendeur trouvé"
              }
            />

            {/* Affichage des boutiques existantes du vendeur sélectionné */}
            {formData.vendorId && selectedVendorBoutiques.length > 0 && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Boutiques existantes de ce vendeur ({selectedVendorBoutiques.length}/4)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedVendorBoutiques.map((boutique) => (
                    <Box 
                      key={boutique.id} 
                      sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        backgroundColor: '#f5f5f5'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {boutique.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {boutique.address}, {boutique.quartier}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Statut: {boutique.isActive ? 'Actif' : 'Inactif'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Message d'information sur la limite */}
            {formData.vendorId && selectedVendorBoutiques.length >= 4 && !boutique && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Ce vendeur a déjà atteint la limite de 4 boutiques maximum.
              </Alert>
            )}

            <TextField
              name="name"
              label="Nom de la boutique"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
            />

            {/* Upload du logo */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Logo de la boutique
              </Typography>
              
              {(logoPreview || formData.logo) && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <img
                    src={logoPreview || formData.logo}
                    alt="Aperçu du logo"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px'
                    }}
                    onError={(e) => {
                      console.error('Erreur de chargement de l\'image:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={removeLogo}
                      disabled={uploadingLogo}
                    >
                      Supprimer
                    </Button>
                  </Box>
                </Box>
              )}

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload"
                type="file"
                onChange={handleLogoChange}
                disabled={uploadingLogo}
              />
              <label htmlFor="logo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={uploadingLogo}
                  startIcon={uploadingLogo ? <CircularProgress size={20} /> : null}
                >
                  {uploadingLogo ? 'Upload en cours...' : logoPreview ? 'Changer le logo' : 'Sélectionner un logo'}
                </Button>
              </label>
              
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                Formats acceptés: JPEG, JPG, PNG, GIF, WEBP (max 50MB - sera compressé en WebP)
              </Typography>
            </Box>

            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="address"
                label="Adresse"
                value={formData.address}
                onChange={handleChange}
                required
                fullWidth
              />
              <TextField
                name="quartier"
                label="Quartier"
                value={formData.quartier}
                onChange={handleChange}
                required
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="phone"
                label="Téléphone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="whatsapp"
                label="WhatsApp"
                value={formData.whatsapp}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            <TextField
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              name="openingHours"
              label="Heures d'ouverture"
              value={formData.openingHours}
              onChange={handleChange}
              placeholder="Ex: Lundi - Vendredi: 9h - 18h"
              fullWidth
            />

            <Autocomplete
              multiple
              options={specialtyOptions}
              value={formData.specialties}
              onChange={handleSpecialtiesChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Spécialités"
                  placeholder="Sélectionnez les spécialités"
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Sauvegarde...' : (boutique ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BoutiqueModal;
